import { useEffect, useRef, useState } from "react";
import * as Y from "yjs";
import { COLLAB_MAX_ENCODED_BYTES, MODULE_ROLES, SOLVE_PHASES, } from "@playbookai/types";
import { useCollabProviderConfig } from "./CollabProvider.js";
import { LegacyProviderRecovery } from "./legacy-provider-recovery.js";
import { SECURE_AUTHORIZATION_CLOSE_REASON_PREFIX_V1 } from "./security/contracts.js";
const SOLVE_PHASE_SET = new Set(SOLVE_PHASES);
/**
 * Read-only iff the provider's authorized scope is `"readonly"` (Hocuspocus
 * sets it from the auth ack). `undefined` (pre-auth) is treated as read-write
 * so the canvas isn't gated during the connect flicker.
 */
export function readOnlyFromScope(scope) {
    return scope === "readonly";
}
/**
 * A collab `editability` stateless message (ADR-0019) → the new read-only
 * value, or `null` for any other stateless payload (e.g. `lock_state`) so the
 * caller leaves read-only untouched. Malformed JSON is ignored (null).
 */
export function readOnlyFromStateless(payload) {
    try {
        const msg = JSON.parse(payload);
        if (msg.type === "editability" && typeof msg.readOnly === "boolean") {
            return msg.readOnly;
        }
        return null;
    }
    catch {
        return null;
    }
}
/**
 * A collab `lock_state` stateless message (ADR-0019) carries document-wide
 * lock facts. Invalid or unrelated payloads return `null` so callers leave the
 * current REST-backed snapshot untouched.
 */
export function lockStateFromStateless(payload) {
    try {
        const msg = JSON.parse(payload);
        if (msg.type !== "lock_state" ||
            typeof msg.canvasLocked !== "boolean" ||
            !Array.isArray(msg.lockedPhases)) {
            return null;
        }
        const lockedPhases = [];
        for (const phase of msg.lockedPhases) {
            if (typeof phase !== "string" || !SOLVE_PHASE_SET.has(phase)) {
                return null;
            }
            lockedPhases.push(phase);
        }
        return { canvasLocked: msg.canvasLocked, lockedPhases };
    }
    catch {
        return null;
    }
}
/**
 * A collab `facilitator` stateless message (ADR-0019) → THIS connection's new
 * `isFacilitator` value, or `null` for any other stateless payload (e.g.
 * `lock_state`/`editability`) so the caller leaves it untouched. Malformed JSON
 * is ignored (null).
 */
export function facilitatorFromStateless(payload) {
    try {
        const msg = JSON.parse(payload);
        if (msg.type === "facilitator" && typeof msg.isFacilitator === "boolean") {
            return msg.isFacilitator;
        }
        return null;
    }
    catch {
        return null;
    }
}
/**
 * A collab `widget_changed` stateless message (qa-004) → the `widgetId` whose
 * REST content changed, or `null` for any other/malformed payload. The peer
 * refetches that one widget; unlike lock/facilitator this is an EVENT, not
 * reactive state, so `useCollabDoc` fires a callback per message.
 */
export function widgetChangedFromStateless(payload) {
    try {
        const msg = JSON.parse(payload);
        if (msg.type === "widget_changed" && typeof msg.widgetId === "string") {
            return msg.widgetId;
        }
        return null;
    }
    catch {
        return null;
    }
}
/** Parse a content-free product-resource invalidation nudge. */
export function resourceChangedFromStateless(payload) {
    try {
        const msg = JSON.parse(payload);
        if (msg.type !== "resource_changed" ||
            msg.protocolVersion !== 1 ||
            (msg.resourceType !== "solve" &&
                msg.resourceType !== "vsm" &&
                msg.resourceType !== "hoshin") ||
            typeof msg.resourceId !== "string" ||
            msg.resourceId.length === 0 ||
            !Array.isArray(msg.fields) ||
            msg.fields.length === 0 ||
            !msg.fields.every((field) => typeof field === "string" && field.length > 0)) {
            return null;
        }
        return {
            type: "resource_changed",
            protocolVersion: 1,
            resourceType: msg.resourceType,
            resourceId: msg.resourceId,
            fields: msg.fields,
        };
    }
    catch {
        return null;
    }
}
/**
 * A server-trusted authorization lifecycle event carried over the authenticated
 * collaboration socket. Malformed and unrelated payloads are ignored.
 */
export function authorizationEventFromStateless(payload) {
    try {
        const input = JSON.parse(payload);
        if (typeof input !== "object" || input === null || Array.isArray(input)) {
            return null;
        }
        const event = input;
        if (event["kind"] === "accessRevoked") {
            const reason = event["reason"];
            if ((reason === "member_removed" ||
                reason === "resource_deleted" ||
                reason === "session_revoked") &&
                Object.keys(event).sort().join(",") === "kind,reason") {
                return { kind: "accessRevoked", reason };
            }
            return null;
        }
        if (event["kind"] !== "authorizationChanged")
            return null;
        const generation = event["resourceAuthorizationGeneration"];
        const reason = event["reason"];
        const effect = event["effect"];
        const baseRole = moduleRoleFromUnknown(event["baseRole"]);
        const expectedKeys = reason === "role_changed"
            ? "baseRole,effect,kind,reason,resourceAuthorizationGeneration"
            : "effect,kind,reason,resourceAuthorizationGeneration";
        if (typeof generation !== "string" ||
            !/^(0|[1-9]\d*)$/.test(generation) ||
            (reason !== "role_changed" &&
                reason !== "lock_changed" &&
                reason !== "facilitator_changed" &&
                reason !== "membership_changed") ||
            (effect !== "replace_document" && effect !== "refresh_roster") ||
            (reason === "role_changed" && baseRole === null) ||
            Object.keys(event).sort().join(",") !== expectedKeys) {
            return null;
        }
        if (reason === "role_changed") {
            if (baseRole === null)
                return null;
            return {
                kind: "authorizationChanged",
                resourceAuthorizationGeneration: generation,
                reason,
                effect,
                baseRole,
            };
        }
        return {
            kind: "authorizationChanged",
            resourceAuthorizationGeneration: generation,
            reason,
            effect,
        };
    }
    catch {
        return null;
    }
}
/** Parse the compact trusted authorization signal on a secure socket close. */
export function authorizationEventFromCloseReason(reason) {
    const parts = reason.split(":");
    if (parts[0] !== SECURE_AUTHORIZATION_CLOSE_REASON_PREFIX_V1)
        return null;
    if (parts[1] === "revoked" && parts.length === 3) {
        const revokedReason = parts[2];
        if (revokedReason === "member_removed" ||
            revokedReason === "resource_deleted" ||
            revokedReason === "session_revoked") {
            return { kind: "accessRevoked", reason: revokedReason };
        }
        return null;
    }
    if (parts[1] === "role") {
        if (parts.length !== 4)
            return null;
        const baseRole = moduleRoleFromUnknown(parts[2]);
        const generation = parts[3];
        if (baseRole === null ||
            generation === undefined ||
            !/^(0|[1-9]\d*)$/.test(generation)) {
            return null;
        }
        return {
            kind: "authorizationChanged",
            resourceAuthorizationGeneration: generation,
            reason: "role_changed",
            effect: "replace_document",
            baseRole,
        };
    }
    if (parts[1] !== "change" || parts.length !== 5)
        return null;
    const eventReason = parts[2];
    const effect = parts[3];
    const generation = parts[4];
    if ((eventReason !== "lock_changed" &&
        eventReason !== "facilitator_changed" &&
        eventReason !== "membership_changed") ||
        (effect !== "replace_document" && effect !== "refresh_roster") ||
        generation === undefined ||
        !/^(0|[1-9]\d*)$/.test(generation)) {
        return null;
    }
    return {
        kind: "authorizationChanged",
        resourceAuthorizationGeneration: generation,
        reason: eventReason,
        effect,
    };
}
function moduleRoleFromUnknown(value) {
    if (typeof value !== "string")
        return null;
    return MODULE_ROLES.find((role) => role === value) ?? null;
}
/**
 * Module-scoped ref-count keyed by docName. React's strict-mode double-mount
 * would otherwise tear down a provider that's about to be re-used by the next
 * mount. The ref-count keeps the provider alive across the strict-mode flip.
 */
const REGISTRY = new Map();
const WRITE_REJECTIONS = new Map();
const WRITE_REJECTION_LISTENERS = new Map();
/**
 * The canonical React hook for binding to a collab document.
 *
 * - `docName` is the full Hocuspocus name (e.g. `solve_canvas:<uuid>`).
 * - Returns the Y.Doc + provider + current connection status. Re-renders on
 *   status change but NOT on Yjs mutations (use `useYMap` / `useYText` for that).
 * - Provider is ref-counted across StrictMode double-mounts.
 */
export function useCollabDoc(docName, _docType, opts) {
    const { wsUrl, getAccessToken } = useCollabProviderConfig();
    const [entry, setEntry] = useState(() => acquire(docName, wsUrl, getAccessToken));
    const { doc, provider } = entry;
    const [initialFacts] = useState(() => entry.facts);
    const [status, setStatus] = useState(provider.status === "connected" ? "connected" : "connecting");
    const [isSynced, setIsSynced] = useState(provider.synced);
    const [readOnly, setReadOnly] = useState(initialFacts?.readOnly ?? readOnlyFromScope(provider.authorizedScope));
    const [lockState, setLockState] = useState(initialFacts?.lockState ?? null);
    const [authoritativeReadOnly, setAuthoritativeReadOnly] = useState(initialFacts?.authoritativeReadOnly ?? null);
    const [liveFacilitator, setLiveFacilitator] = useState(initialFacts?.liveFacilitator ?? null);
    const [boardStatus, setBoardStatus] = useState(initialFacts?.boardStatus ?? null);
    const [writeRejected, setWriteRejected] = useState(() => WRITE_REJECTIONS.get(docName) ?? null);
    const boardStatusRef = useRef(boardStatus);
    const readOnlyRef = useRef(readOnly);
    const keepWarmMsRef = useRef(opts?.keepWarmMs ?? 0);
    useEffect(() => {
        entry.facts = {
            readOnly,
            authoritativeReadOnly,
            lockState,
            liveFacilitator,
            boardStatus,
        };
    }, [
        authoritativeReadOnly,
        boardStatus,
        entry,
        liveFacilitator,
        lockState,
        readOnly,
    ]);
    // Latest `onWidgetChanged` held in a ref so the stateless listener effect
    // (keyed on [docName, provider]) never re-subscribes when the callback's
    // identity changes each render. widget_changed is an EVENT: firing per
    // message avoids the coalescing a single reactive state slot would suffer.
    const onWidgetChangedRef = useRef(opts?.onWidgetChanged);
    const onResourceChangedRef = useRef(opts?.onResourceChanged);
    const onAuthorizationResetRef = useRef(opts?.onAuthorizationReset);
    const authorizationResetGenerationRef = useRef(0);
    useEffect(() => {
        onWidgetChangedRef.current = opts?.onWidgetChanged;
        onResourceChangedRef.current = opts?.onResourceChanged;
        onAuthorizationResetRef.current = opts?.onAuthorizationReset;
        keepWarmMsRef.current = opts?.keepWarmMs ?? 0;
    });
    useEffect(() => {
        const onReplacement = (replacement) => {
            setEntry(replacement);
        };
        entry.replacementListeners.add(onReplacement);
        const rejectionListeners = WRITE_REJECTION_LISTENERS.get(docName) ?? new Set();
        rejectionListeners.add(setWriteRejected);
        WRITE_REJECTION_LISTENERS.set(docName, rejectionListeners);
        const applyReadOnly = (next) => {
            const wasReadOnly = readOnlyRef.current;
            readOnlyRef.current = next;
            if (wasReadOnly && !next)
                provider.forceSync();
            setReadOnly(next);
        };
        const onStatus = ({ status: next, }) => {
            setStatus(next);
        };
        const onSynced = ({ state }) => setIsSynced(state);
        // The auth ack carries the scope; re-derive read-only on every (re)auth.
        const onAuthenticated = () => {
            setAuthoritativeReadOnly(null);
            applyReadOnly(readOnlyFromScope(provider.authorizedScope));
            setLockState(null);
            setLiveFacilitator(null);
        };
        // A live readOnly / lock / facilitator flip arrives as an `editability`,
        // `lock_state`, or `facilitator` stateless message respectively (ADR-0019)
        // — apply it without waiting for a reconnect.
        const onStateless = ({ payload }) => {
            const next = readOnlyFromStateless(payload);
            if (next !== null) {
                applyReadOnly(next);
                setAuthoritativeReadOnly(next);
            }
            const nextLockState = lockStateFromStateless(payload);
            if (nextLockState !== null)
                setLockState(nextLockState);
            const nextFacilitator = facilitatorFromStateless(payload);
            if (nextFacilitator !== null)
                setLiveFacilitator(nextFacilitator);
            // widget_changed is an event, not a fact — fire the callback per message
            // (the ref keeps this listener stable). The host refetches that widget.
            const changedWidgetId = widgetChangedFromStateless(payload);
            if (changedWidgetId !== null)
                onWidgetChangedRef.current?.(changedWidgetId);
            const changedResource = resourceChangedFromStateless(payload);
            if (changedResource !== null)
                onResourceChangedRef.current?.(changedResource);
            const nextBoardStatus = boardStatusFromStateless(payload);
            if (nextBoardStatus) {
                const current = boardStatusRef.current;
                if (!current ||
                    nextBoardStatus.documentVersion > current.documentVersion ||
                    (nextBoardStatus.documentVersion === current.documentVersion &&
                        sameBoardStatus(current, nextBoardStatus))) {
                    boardStatusRef.current = nextBoardStatus;
                    setBoardStatus(nextBoardStatus);
                    if (nextBoardStatus.sizeState === "read_only")
                        applyReadOnly(true);
                }
            }
            const rejection = writeRejectedFromStateless(payload);
            if (rejection) {
                setWriteRejection(docName, rejection);
                replaceRegistryEntry(docName, entry, wsUrl, getAccessToken);
            }
        };
        const onClose = ({ event }) => {
            const authorizationEvent = authorizationEventFromCloseReason(event.reason);
            const onAuthorizationReset = onAuthorizationResetRef.current;
            if (authorizationEvent === null || !onAuthorizationReset)
                return;
            entry.awaitingAuthorization = true;
            provider.disconnect();
            readOnlyRef.current = true;
            setReadOnly(true);
            setStatus("disconnected");
            setIsSynced(false);
            setLockState(null);
            setLiveFacilitator(null);
            const resetGeneration = ++authorizationResetGenerationRef.current;
            let resumed = false;
            onAuthorizationReset({
                resumeAfterAccessVerified: () => {
                    if (resumed ||
                        authorizationResetGenerationRef.current !== resetGeneration) {
                        return;
                    }
                    resumed = true;
                    entry.awaitingAuthorization = false;
                    void provider.connect().catch(() => {
                        if (authorizationResetGenerationRef.current !== resetGeneration) {
                            return;
                        }
                        setStatus("disconnected");
                        setIsSynced(false);
                    });
                },
            });
        };
        provider.on("status", onStatus);
        provider.on("synced", onSynced);
        provider.on("authenticated", onAuthenticated);
        provider.on("stateless", onStateless);
        provider.on("close", onClose);
        // Events that arrived while the document was warm with no view.
        const missed = entry.missed;
        entry.missed = null;
        for (const widgetId of missed?.widgets ?? []) {
            onWidgetChangedRef.current?.(widgetId);
        }
        for (const change of missed?.resources.values() ?? []) {
            onResourceChangedRef.current?.(change);
        }
        return () => {
            authorizationResetGenerationRef.current += 1;
            provider.off("status", onStatus);
            provider.off("synced", onSynced);
            provider.off("authenticated", onAuthenticated);
            provider.off("stateless", onStateless);
            provider.off("close", onClose);
            entry.replacementListeners.delete(onReplacement);
            rejectionListeners.delete(setWriteRejected);
            if (rejectionListeners.size === 0) {
                WRITE_REJECTION_LISTENERS.delete(docName);
            }
            release(docName, entry, keepWarmMsRef.current);
        };
    }, [docName, entry, getAccessToken, provider, wsUrl]);
    return {
        doc,
        provider,
        status,
        isSynced,
        readOnly,
        authoritativeReadOnly,
        lockState,
        liveFacilitator,
        boardStatus,
        writeRejected,
        dismissWriteRejected: () => setWriteRejection(docName, null),
        awareness: provider.awareness,
        stop: entry.recovery.stop,
        retry: entry.recovery.retry,
    };
}
function acquire(docName, wsUrl, getAccessToken) {
    const existing = REGISTRY.get(docName);
    if (existing) {
        if (existing.warm)
            wake(existing);
        existing.refCount += 1;
        return existing;
    }
    const entry = createEntry(docName, wsUrl, getAccessToken, {
        refCount: 1,
        generation: 1,
        replacementListeners: new Set(),
    });
    REGISTRY.set(docName, entry);
    return entry;
}
/**
 * A new document and provider. The provider never sends an empty sign-in and
 * heals itself after a refused one (NET-BUG-28); see
 * `legacy-provider-recovery.ts`.
 */
function createEntry(docName, wsUrl, getAccessToken, shared) {
    const doc = new Y.Doc();
    const recovery = new LegacyProviderRecovery({
        url: wsUrl,
        name: docName,
        document: doc,
        getTicket: getAccessToken,
        awaitingAuthorization: () => entry.awaitingAuthorization,
    });
    const entry = {
        doc,
        provider: recovery.provider,
        recovery,
        ...shared,
        facts: null,
        warm: null,
        missed: null,
        awaitingAuthorization: false,
    };
    return entry;
}
function release(docName, expected, keepWarmMs = 0) {
    const entry = REGISTRY.get(docName);
    if (!entry || entry !== expected)
        return;
    entry.refCount -= 1;
    if (entry.refCount > 0)
        return;
    // A document waiting for its host to verify access is never kept: no view
    // would resume it (review R6).
    if (keepWarmMs > 0 && !entry.awaitingAuthorization) {
        keepWarm(docName, entry, keepWarmMs);
        return;
    }
    teardown(docName, entry);
    WRITE_REJECTIONS.delete(docName);
}
function teardown(docName, entry) {
    if (entry.warm) {
        clearTimeout(entry.warm.timer);
        entry.warm.stop();
        entry.warm = null;
    }
    destroyProvider(entry);
    entry.doc.destroy();
    if (REGISTRY.get(docName) === entry)
        REGISTRY.delete(docName);
}
/**
 * Destroy a provider and the socket it made from its `url`, after stopping its
 * recovery. The provider's own `destroy()` only detaches from that socket
 * (Hocuspocus keeps it, as `preserveConnection` is on by default), so when the
 * server then closes it the socket opens a new, empty connection that stays
 * for the life of the page. The socket goes after the provider has sent its
 * close message on it.
 */
function destroyProvider({ provider, recovery }) {
    recovery.dispose();
    // A test double of the provider can have no configuration.
    const socket = provider.configuration?.websocketProvider;
    provider.destroy();
    socket?.destroy();
}
/**
 * No view holds the document: keep it synced for `ms`, with the local
 * presence cleared, so a view that comes back finds it ready. The facts the
 * server sends meanwhile are kept on the entry; widget and resource events
 * are kept for the next view. A dropped authorization or a write rejection
 * ends the wait, and the next view connects from the start.
 */
function keepWarm(docName, entry, ms) {
    const { provider } = entry;
    provider.awareness?.setLocalState(null);
    const missed = (entry.missed ??= {
        widgets: new Set(),
        resources: new Map(),
    });
    const setFacts = (patch) => {
        const current = entry.facts ?? {
            readOnly: readOnlyFromScope(provider.authorizedScope),
            authoritativeReadOnly: null,
            lockState: null,
            liveFacilitator: null,
            boardStatus: null,
        };
        entry.facts = { ...current, ...patch };
    };
    const onStateless = ({ payload }) => {
        const rejection = writeRejectedFromStateless(payload);
        if (rejection) {
            setWriteRejection(docName, rejection);
            teardown(docName, entry);
            return;
        }
        const nextReadOnly = readOnlyFromStateless(payload);
        if (nextReadOnly !== null) {
            setFacts({ readOnly: nextReadOnly, authoritativeReadOnly: nextReadOnly });
        }
        const nextLockState = lockStateFromStateless(payload);
        if (nextLockState !== null)
            setFacts({ lockState: nextLockState });
        const nextFacilitator = facilitatorFromStateless(payload);
        if (nextFacilitator !== null)
            setFacts({ liveFacilitator: nextFacilitator });
        const nextBoardStatus = boardStatusFromStateless(payload);
        if (nextBoardStatus) {
            const current = entry.facts?.boardStatus ?? null;
            if (!current ||
                nextBoardStatus.documentVersion > current.documentVersion) {
                setFacts({
                    boardStatus: nextBoardStatus,
                    ...(nextBoardStatus.sizeState === "read_only"
                        ? { readOnly: true }
                        : {}),
                });
            }
        }
        const widgetId = widgetChangedFromStateless(payload);
        if (widgetId !== null)
            missed.widgets.add(widgetId);
        const resource = resourceChangedFromStateless(payload);
        if (resource !== null) {
            missed.resources.set(JSON.stringify(resource), resource);
        }
    };
    // A re-join resets the facts, as it does for a view.
    const onAuthenticated = () => {
        entry.facts = null;
    };
    const onClose = ({ event }) => {
        if (authorizationEventFromCloseReason(event.reason) !== null) {
            teardown(docName, entry);
        }
    };
    provider.on("stateless", onStateless);
    provider.on("authenticated", onAuthenticated);
    provider.on("close", onClose);
    entry.warm = {
        timer: setTimeout(() => {
            teardown(docName, entry);
            WRITE_REJECTIONS.delete(docName);
        }, ms),
        stop: () => {
            provider.off("stateless", onStateless);
            provider.off("authenticated", onAuthenticated);
            provider.off("close", onClose);
        },
    };
}
/** A view takes a warm document back: stop the wait and restore presence. */
function wake(entry) {
    if (!entry.warm)
        return;
    clearTimeout(entry.warm.timer);
    entry.warm.stop();
    entry.warm = null;
    const awareness = entry.provider.awareness;
    if (awareness && awareness.getLocalState() === null) {
        awareness.setLocalState({});
    }
}
function replaceRegistryEntry(docName, expected, wsUrl, getAccessToken) {
    if (REGISTRY.get(docName) !== expected)
        return;
    const replacement = createEntry(docName, wsUrl, getAccessToken, {
        refCount: expected.refCount,
        generation: expected.generation + 1,
        replacementListeners: expected.replacementListeners,
    });
    REGISTRY.set(docName, replacement);
    destroyProvider(expected);
    expected.doc.destroy();
    for (const listener of [...replacement.replacementListeners]) {
        listener(replacement);
    }
}
export function boardStatusFromStateless(payload) {
    try {
        const value = JSON.parse(payload);
        if (value["type"] !== "board_status_v1" ||
            value["protocolVersion"] !== 1 ||
            !Number.isSafeInteger(value["documentVersion"]) ||
            !Number.isSafeInteger(value["encodedBytes"]) ||
            (value["sizeState"] !== "normal" &&
                value["sizeState"] !== "warning" &&
                value["sizeState"] !== "read_only")) {
            return null;
        }
        const capacity = value["capacity"];
        if (capacity !== undefined && !validCapacity(capacity))
            return null;
        return value;
    }
    catch {
        return null;
    }
}
export function writeRejectedFromStateless(payload) {
    try {
        const value = JSON.parse(payload);
        return value["type"] === "board_write_rejected_v1" &&
            value["protocolVersion"] === 1 &&
            value["reason"] === "crossing_too_large" &&
            Number.isSafeInteger(value["attemptedEncodedBytes"]) &&
            value["maxEncodedBytes"] === COLLAB_MAX_ENCODED_BYTES
            ? value
            : null;
    }
    catch {
        return null;
    }
}
function setWriteRejection(docName, notice) {
    if (notice)
        WRITE_REJECTIONS.set(docName, notice);
    else
        WRITE_REJECTIONS.delete(docName);
    for (const listener of WRITE_REJECTION_LISTENERS.get(docName) ?? []) {
        listener(notice);
    }
}
function sameBoardStatus(left, right) {
    return JSON.stringify(left) === JSON.stringify(right);
}
function validCapacity(value) {
    if (typeof value !== "object" || value === null || Array.isArray(value)) {
        return false;
    }
    const record = value;
    const numeric = [
        "uniquePeople",
        "liveTabs",
        "editingTabs",
        "generalEditorsUsed",
        "reservedEditorsUsed",
        "generalConnectionsUsed",
        "reservedConnectionsUsed",
    ];
    return (Object.keys(record).length === 9 &&
        numeric.every((key) => Number.isSafeInteger(record[key]) && record[key] >= 0) &&
        (record["warningState"] === "normal" ||
            record["warningState"] === "warning") &&
        (record["capacityState"] === "available" ||
            record["capacityState"] === "editor_full" ||
            record["capacityState"] === "connection_full"));
}
/** Test-only: reset the registry between specs. */
export function __resetCollabDocRegistry() {
    for (const entry of REGISTRY.values()) {
        if (entry.warm)
            clearTimeout(entry.warm.timer);
        destroyProvider(entry);
        entry.doc.destroy();
    }
    REGISTRY.clear();
    WRITE_REJECTIONS.clear();
    WRITE_REJECTION_LISTENERS.clear();
}
//# sourceMappingURL=useCollabDoc.js.map